import {
  ActionIcon,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import React, {useRef, useState} from 'react';

import ModelHubModelIdentity from 'model-hub/lib/ModelHubModelIdentity';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import UseMsaCheckbox from './UseMsaCheckbox';

interface ModelSectionProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  msaEnabled: boolean;
  onMSAEnabledChange: (enabled: boolean) => void;
  onModelChange: (model: ScientificModelNode) => void;
  onRemoveModel: () => void;
  selectedModel: ScientificModelNode | null;
}

const MODEL_CARD_MIN_WIDTH_PX = 336;

export default function ModelSection({
  availableModels,
  msaEnabled,
  onMSAEnabledChange,
  onModelChange,
  onRemoveModel,
  selectedModel,
}: ModelSectionProps): JSX.Element {
  const {classes} = useStyles();
  const addModelCardRef = useRef<HTMLButtonElement>(null);
  const [activeMenu, setActiveMenu] = useState<ModelMenuAnchor | null>(null);
  const [cardMenuAnchor, setCardMenuAnchor] = useState<{x: number; y: number}>({x: 0, y: 0});
  const addableModels = availableModels.filter(
    (model) => model.externalReference !== selectedModel?.externalReference
  );

  const handleMenuChange = (anchor: ModelMenuAnchor) => (opened: boolean) => {
    setActiveMenu(opened ? anchor : null);
  };

  const openCardMenuAtPoint = (x: number, y: number): void => {
    setCardMenuAnchor({x, y});
    setActiveMenu('card');
  };

  const handleAddModelCardClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    openCardMenuAtPoint(event.clientX, event.clientY);
  };

  const handleAddModelCardKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    const rect = addModelCardRef.current?.getBoundingClientRect();
    if (rect == null) {
      return;
    }
    openCardMenuAtPoint(rect.left + rect.width / 2, rect.bottom);
  };

  const handleSelectModel = (model: ScientificModelNode): void => {
    setActiveMenu(null);
    onModelChange(model);
  };

  return (
    <Paper aria-label="Model settings" p="lg" radius="md" role="group">
      <Stack spacing="md">
        {selectedModel == null ? (
          <>
            <Group align="center" noWrap={true} spacing={4}>
              <Title mb={0} order={2}>
                Model
              </Title>
              <Menu
                onChange={handleMenuChange('header')}
                opened={activeMenu === 'header'}
                position="bottom-start"
                withinPortal={true}
              >
                <Menu.Target>
                  <ActionIcon aria-label="Add model" size="md" variant="subtle">
                    <BenchlingIcon icon="plus" />
                  </ActionIcon>
                </Menu.Target>
                <ModelPickerDropdown addableModels={addableModels} onSelectModel={handleSelectModel} />
              </Menu>
            </Group>
            <Menu
              onChange={handleMenuChange('card')}
              opened={activeMenu === 'card'}
              position="bottom-start"
              positionDependencies={[cardMenuAnchor.x, cardMenuAnchor.y]}
              withinPortal={true}
            >
              <Menu.Target>
                <div
                  aria-hidden={true}
                  className={classes.menuPositionAnchor}
                  style={{left: cardMenuAnchor.x, top: cardMenuAnchor.y}}
                />
              </Menu.Target>
              <ModelPickerDropdown addableModels={addableModels} onSelectModel={handleSelectModel} />
            </Menu>
            <UnstyledButton
              ref={addModelCardRef}
              aria-expanded={activeMenu === 'card'}
              aria-haspopup="menu"
              aria-label="Choose a model"
              className={classes.addModelCard}
              onClick={handleAddModelCardClick}
              onKeyDown={handleAddModelCardKeyDown}
              type="button"
            >
              <Stack align="center" spacing="md">
                <div className={classes.addModelIconTile}>
                  <BenchlingIcon className={classes.addModelIcon} icon="hexagon-nodes" />
                </div>
                <Text className={classes.addModelTitle} size="md">
                  Add a model
                </Text>
              </Stack>
            </UnstyledButton>
          </>
        ) : (
          <Group align="center" noWrap={true} spacing={4}>
            <Title mb={0} order={2}>
              Model
            </Title>
          </Group>
        )}
        {selectedModel == null ? null : (
          <div className={classes.modelCardGrid}>
            <Paper className={classes.modelCard} p="md" radius="md" withBorder={true}>
              <Stack spacing="md">
                <Group align="flex-start" noWrap={true} position="apart">
                  <ModelHubModelIdentity className={classes.modelIdentity} model={selectedModel} />
                  <ActionIcon aria-label="Remove model" onClick={onRemoveModel} variant="subtle">
                    <BenchlingIcon icon="trash-can-outline" />
                  </ActionIcon>
                </Group>
                <UseMsaCheckbox
                  model={selectedModel}
                  msaEnabled={msaEnabled}
                  onMSAEnabledChange={onMSAEnabledChange}
                />
              </Stack>
            </Paper>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

type ModelMenuAnchor = 'card' | 'header';

interface ModelPickerDropdownProps {
  addableModels: ReadonlyArray<ScientificModelNode>;
  onSelectModel: (model: ScientificModelNode) => void;
}

function ModelPickerDropdown({addableModels, onSelectModel}: ModelPickerDropdownProps): JSX.Element {
  return (
    <Menu.Dropdown>
      {addableModels.map((model) => (
        <Menu.Item
          key={model.externalReference}
          onClick={() => {
            onSelectModel(model);
          }}
        >
          {model.displayName}
        </Menu.Item>
      ))}
    </Menu.Dropdown>
  );
}

const useStyles = createStyles((theme) => ({
  addModelCard: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
      borderColor: theme.colors.gray[5],
    },
    border: `2px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    display: 'block',
    padding: `${theme.spacing.xl} ${theme.spacing.lg}`,
    transition: 'background-color 150ms ease, border-color 150ms ease',
    width: '100%',
  },
  addModelIconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.gray[1],
    borderRadius: theme.radius.lg,
    display: 'flex',
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  addModelIcon: {
    color: theme.colors.gray[6],
    fontSize: 22,
  },
  addModelTitle: {
    fontWeight: 600,
  },
  menuPositionAnchor: {
    height: 0,
    pointerEvents: 'none',
    position: 'fixed',
    width: 0,
  },
  modelCardGrid: {
    display: 'grid',
    gap: theme.spacing.lg,
    gridTemplateColumns: `repeat(auto-fill, minmax(${MODEL_CARD_MIN_WIDTH_PX}px, 1fr))`,
  },
  modelCard: {
    height: '100%',
  },
  modelIdentity: {
    flex: 1,
  },
}));
